/**
 * Product images. On the product page (desktop) they sit in a grid; on phones, and anywhere inside
 * quick view, they become a slideshow with dots.
 */
class ProductMediaGrid extends HTMLElement {
  static observedAttributes = ['class'];

  connectedCallback() {
    this.list = this.querySelector('[ref="list"]');
    if (!this.list) return;

    this.media = window.matchMedia('(max-width: 749px)');
    this.media.addEventListener('change', this.#setMode);
    this.#setMode();

    this.list.addEventListener('scroll', this.#onScroll, { passive: true });
    this.list.addEventListener('mousedown', this.#onMouseDown);
    this.list.addEventListener('dragstart', this.#onDragStart);
    this.addEventListener('click', this.#onDotClick);
  }

  /**
   * Quick view reuses this element for each product it opens, and the morph that swaps the product
   * in resets the class from the server HTML. Put slideshow mode back and start at the first image.
   */
  attributeChangedCallback() {
    if (!this.list || !this.media) return;
    const wasSlideshow = this.#isSlideshow;
    this.#setMode();
    if (!wasSlideshow && this.#isSlideshow) {
      this.list.scrollLeft = 0;
      this.#onScroll();
    }
  }

  /** Dots change with each product shown, so look them up when needed */
  get #dots() {
    return [...this.querySelectorAll('.product-media-grid__dot')];
  }

  /** @param {MouseEvent} event */
  #onDotClick = (event) => {
    const dot = event.target instanceof Element ? event.target.closest('.product-media-grid__dot') : null;
    if (dot) this.#go(this.#dots.indexOf(dot));
  };

  disconnectedCallback() {
    this.list?.removeEventListener('scroll', this.#onScroll);
    this.list?.removeEventListener('mousedown', this.#onMouseDown);
    this.list?.removeEventListener('dragstart', this.#onDragStart);
    this.media?.removeEventListener('change', this.#setMode);
    this.removeEventListener('click', this.#onDotClick);
  }

  /** Slideshow in quick view and on phones, grid otherwise */
  #setMode = () => {
    const slideshow = Boolean(this.closest('.quick-add-modal, dialog')) || this.media.matches;
    this.classList.toggle('product-media-grid--slideshow', slideshow);
  };

  get #index() {
    return Math.round(this.list.scrollLeft / (this.list.clientWidth || 1));
  }

  #go(index) {
    const items = this.list.children;
    const target = items[(index + items.length) % items.length];
    if (target instanceof HTMLElement) this.list.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  get #isSlideshow() {
    return this.classList.contains('product-media-grid--slideshow');
  }

  /** Stop the browser dragging the image itself when the slideshow is dragged with a mouse */
  #onDragStart = (event) => {
    if (this.#isSlideshow) event.preventDefault();
  };

  /** Mouse drag to change slide. Touch uses the native scroll */
  #onMouseDown = (event) => {
    if (event.button !== 0 || !this.#isSlideshow || this.list.children.length < 2) return;
    if (event.target instanceof Element && event.target.closest('model-viewer, video, iframe')) return;

    event.preventDefault();
    const start = event.clientX;
    const startLeft = this.list.scrollLeft;
    const startIndex = this.#index;
    let moved = 0;

    const onMove = (/** @type {MouseEvent} */ move) => {
      moved = move.clientX - start;
      if (Math.abs(moved) > 3) this.list.setAttribute('dragging', '');
      this.list.scrollLeft = startLeft - moved;
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (!this.list.hasAttribute('dragging')) return;

      // Swallow the click that follows a drag
      this.list.addEventListener('click', (click) => click.preventDefault(), { capture: true, once: true });
      setTimeout(() => this.list.removeAttribute('dragging'));

      const threshold = Math.min(60, this.list.clientWidth / 5);
      const step = Math.abs(moved) > threshold ? -Math.sign(moved) : 0;
      const last = this.list.children.length - 1;
      this.#go(Math.max(0, Math.min(last, startIndex + step)));
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  #onScroll = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      const index = this.#index;
      this.#dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    });
  };
}

if (!customElements.get('product-media-grid')) customElements.define('product-media-grid', ProductMediaGrid);
